import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Navbar } from "../components/Navbar";
import { FeaturedBadge } from "../components/Featured";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Search, MapPin, Wrench, X, User, Phone, MessageSquare, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TICINO_LOCATIONS = [
  { value: "all", label: "Tutte le località" },
  { value: "Residente in Italia (Frontaliere)", label: "Frontaliere (Italia)" },
  { value: "Lugano", label: "Lugano" },
  { value: "Bellinzona", label: "Bellinzona" },
  { value: "Locarno", label: "Locarno" },
  { value: "Mendrisio", label: "Mendrisio" },
  { value: "Chiasso", label: "Chiasso" }
];

export default function ServicesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [filters, setFilters] = useState({
    search: searchParams.get("search") || "",
    location: searchParams.get("location") || "all"
  });

  useEffect(() => {
    fetchServices();
  }, [searchParams]);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchParams.get("search")) params.append("search", searchParams.get("search"));
      if (searchParams.get("location") && searchParams.get("location") !== "all") params.append("location", searchParams.get("location"));
      
      const response = await axios.get(`${API}/services?${params.toString()}`);
      setServices(response.data);
    } catch (error) {
      console.error("Failed to fetch services:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const newParams = new URLSearchParams();
    if (filters.search) newParams.set("search", filters.search);
    if (filters.location && filters.location !== "all") newParams.set("location", filters.location);
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setFilters({ search: "", location: "all" });
    setSearchParams(new URLSearchParams());
  };

  const hasActiveFilters = filters.search || (filters.location && filters.location !== "all");

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="services-page">
      <Navbar />
      
      <div className="bg-emerald-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl font-bold mb-2">Zona Servizi</h1>
          <p className="text-emerald-100">Trova professionisti e fornitori di servizi in Canton Ticino</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg border border-slate-200 p-4 mb-6">
          <form onSubmit={handleSearch}>
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input type="text" placeholder="Cerca servizi..." value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} className="pl-10" />
              </div>
              <Select value={filters.location} onValueChange={(value) => setFilters({ ...filters, location: value })}>
                <SelectTrigger className="w-full lg:w-[180px]">
                  <MapPin className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Località" />
                </SelectTrigger>
                <SelectContent>
                  {TICINO_LOCATIONS.map((loc) => <SelectItem key={loc.value} value={loc.value}>{loc.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white">Cerca</Button>
            </div>
            {hasActiveFilters && (
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-200">
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-emerald-600">
                  <X className="w-4 h-4 mr-1" /> Rimuovi filtri
                </Button>
              </div>
            )}
          </form>
        </div>

        <div className="mb-4">
          <p className="text-slate-600">{loading ? "Caricamento..." : `${services.length} servizi trovati`}</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner"></div></div>
        ) : services.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <Link key={service.id} to={`/services/${service.id}`} className="group">
                <Card className={`hover:shadow-lg transition-shadow overflow-hidden h-full ${service.is_featured ? "ring-2 ring-amber-400" : ""}`}>
                  {/* Immagine Servizio */}
                  {service.image_url ? (
                    <div className="w-full h-40 bg-slate-100">
                      <img 
                        src={service.image_url.startsWith('http') 
                          ? service.image_url 
                          : `${process.env.REACT_APP_BACKEND_URL}${service.image_url}`
                        }
                        alt={service.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        onError={(e) => { e.target.onerror = null; e.target.src = '/logo-transparent.png'; e.target.className = 'w-full h-full object-contain p-8'; }}
                        data-testid={`service-card-image-${service.id}`}
                      />
                    </div>
                  ) : (
                    <div className="w-full h-40 bg-slate-50 border-b border-slate-100 flex items-center justify-center" data-testid={`service-card-logo-${service.id}`}>
                      <img src="/logo-transparent.png" alt="LavoroTicino.ch" loading="lazy" className="h-20 object-contain" />
                    </div>
                  )}
                  <CardContent className="p-5">
                    <FeaturedBadge item={service} className="mb-2" />
                    <div className="flex items-start justify-between mb-2">
                      {service.tariffa && (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          {service.tariffa}{!service.tariffa.toLowerCase().includes('chf') && ' CHF'}
                        </Badge>
                      )}
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-emerald-600 transition-colors">{service.title}</h3>
                    <p className="text-slate-600 text-sm mb-3 line-clamp-2">{service.description}</p>
                    
                    <div className="space-y-1.5 text-sm text-slate-500">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        <span>{service.provider_name}</span>
                      </div>
                      {service.location && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          <span>{service.location}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-lg border border-slate-200">
            <Wrench className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Nessun servizio trovato</h3>
            {hasActiveFilters && <Button variant="outline" onClick={clearFilters}>Rimuovi filtri</Button>}
          </div>
        )}
      </div>
    </div>
  );
}
